import React from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Product } from '../../services/inventory.service';
import { useProductLocations } from '../../hooks/useWarehouses';
import { Modal } from '../ui/Modal';
import {
  DetailCard,
  DetailItem,
  FormActions,
  badgeClass,
  formatMoney,
  formatNumber,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';

interface ProductDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  onEdit?: (product: Product) => void;
}

const getStockStatus = (product: Product) => {
  if (Number(product.currentStock) <= Number(product.minimumStock)) {
    return { label: 'Low Stock', badge: 'text-red-700 bg-red-50 ring-red-600/20', bar: 'bg-red-500' };
  }
  if (Number(product.currentStock) >= Number(product.maximumStock)) {
    return { label: 'High Stock', badge: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20', bar: 'bg-yellow-500' };
  }
  return { label: 'Normal', badge: 'text-green-700 bg-green-50 ring-green-600/20', bar: 'bg-green-500' };
};

const formatTimestamp = (value?: string) =>
  value ? format(new Date(value), 'dd MMM yyyy, hh:mm a') : '—';

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  isOpen,
  onClose,
  product,
  onEdit,
}) => {
  // Asked for only while the dialog shows a product
  const { data: locations = [], isLoading: locationsLoading } = useProductLocations(isOpen ? product?.id : undefined);
  if (!product) return null;

  const status = getStockStatus(product);
  const maximumStock = Number(product.maximumStock) || 0;
  const stockPercent =
    maximumStock > 0
      ? Math.min(100, Math.max(0, (Number(product.currentStock) / maximumStock) * 100))
      : 0;
  const price = Number(product.price);
  const costPrice = Number(product.costPrice);
  const margin = price - costPrice;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Product Details" size="lg">
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="text-lg font-semibold text-gray-900">{product.name}</h4>
            <p className="mt-1 text-sm text-gray-500">
              <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                {product.sku}
              </span>
              {product.category && <span className="ml-2">{product.category}</span>}
            </p>
          </div>
          <span className={`${badgeClass} ${status.badge}`}>{status.label}</span>
        </div>

        {product.description && <p className="text-sm text-gray-600">{product.description}</p>}

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-baseline justify-between">
            <p className="text-sm text-gray-500">Stock on hand</p>
            <p className="text-sm text-gray-500">
              Min {formatNumber(product.minimumStock)} · Max {formatNumber(product.maximumStock)}
            </p>
          </div>
          <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
            {formatNumber(product.currentStock)}{' '}
            <span className="text-sm font-normal text-gray-500">{product.unit}</span>
          </p>
          <div className="mt-3 h-2 w-full rounded-full bg-gray-200">
            <div className={`h-2 rounded-full ${status.bar}`} style={{ width: `${stockPercent}%` }} />
          </div>
        </div>

        <DetailCard title="Pricing" columns={4}>
          <DetailItem label="Selling price">{formatMoney(price)}</DetailItem>
          <DetailItem label="Cost price">{formatMoney(costPrice)}</DetailItem>
          <DetailItem label="Unit price">{formatMoney(Number(product.unitPrice))}</DetailItem>
          <DetailItem label="Margin per unit">
            <span className={margin < 0 ? 'text-red-600' : undefined}>
              {formatMoney(margin)}
              {price > 0 && ` (${((margin / price) * 100).toFixed(1)}%)`}
            </span>
          </DetailItem>
        </DetailCard>

        <DetailCard title="Inventory" columns={4}>
          <DetailItem label="Stock value (at cost)">
            {formatMoney(Number(product.currentStock) * costPrice)}
          </DetailItem>
          <DetailItem label="Bin / rack">{product.location || '—'}</DetailItem>
          <DetailItem label="HSN / SAC code">{product.hsnCode || '—'}</DetailItem>
          <DetailItem label="Created">{formatTimestamp(product.createdAt)}</DetailItem>
          <DetailItem label="Last updated">{formatTimestamp(product.updatedAt)}</DetailItem>
        </DetailCard>

        <section className="rounded-lg border border-gray-200 bg-white">
          <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Stock by warehouse
          </h4>
          {locations.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500">{locationsLoading ? 'Loading…' : 'No stock in any warehouse.'}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {locations.map((location) => (
                <li key={location.warehouseId} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-900">
                    {location.name} <span className="text-gray-500">({location.code})</span>
                    {location.isDefault && <span className={`${badgeClass} ml-2 bg-indigo-50 text-indigo-700 ring-indigo-600/20`}>Default</span>}
                  </span>
                  <span className="font-medium tabular-nums text-gray-900">
                    {formatNumber(location.quantity)} <span className="font-normal text-gray-500">{product.unit}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <FormActions>
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          Close
        </button>
        {onEdit && (
          <button type="button" onClick={() => onEdit(product)} className={primaryButtonClass}>
            <PencilIcon className="mr-2 h-4 w-4" />
            Edit Product
          </button>
        )}
      </FormActions>
    </Modal>
  );
};

export default ProductDetailsModal;
